import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { and as andMacro, gt as gtMacro, not as notMacro } from '@ember/object/computed';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { take, pick } from '@nullvoxpopuli/ember-composable-helpers';
import { all, task, dropTask, restartableTask } from 'ember-concurrency';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
import set from 'ember-set-helper/helpers/set';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberPopover from 'ember-tooltips/components/ember-popover';
import { and, gt, isEmpty, not } from 'ember-truth-helpers';
import ConfirmDirtyTransition from 'garaje/components/confirm-dirty-transition';
import GlobalFlowIcon from 'garaje/components/global-flow-icon';
import MessageBox from 'garaje/components/ui/classic/message-box';
import preventDefault from 'garaje/helpers/prevent-default';
import type CompanyRoleModel from 'garaje/models/company-role';
import type FlowModel from 'garaje/models/flow';
import type LocationModel from 'garaje/models/location';
import type LocationRoleModel from 'garaje/models/location-role';
import type UserModel from 'garaje/models/user';
import type VisualComplianceConfigurationModel from 'garaje/models/visual-compliance-configuration';
import type ZoneRoleModel from 'garaje/models/zone-role';
import type CurrentLocationService from 'garaje/services/current-location';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { Permission } from 'garaje/utils/ui-permissions';
import LiquidIf from 'liquid-fire/components/liquid-if';

const integration_name = 'Visual Compliance';
const message_code_type = 'integration';

interface VisualComplianceConfigureComponentSignature {
  Args: {
    configuration?: VisualComplianceConfigurationModel;
    location?: LocationModel;
    readOnly?: boolean;
  };
}

export default class VisualComplianceConfigureComponent extends Component<VisualComplianceConfigureComponentSignature> {
  @service declare currentLocation: CurrentLocationService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare store: Store;

  @andMacro('secno', 'password') canConnect!: boolean;
  @andMacro('args.configuration.secno', 'validCredentials') isConnected!: boolean;
  @notMacro('args.configuration.credentialsInvalid') validCredentials!: boolean;
  @gtMacro('connectTask.performCount', 0) didAttemptConnect!: boolean;

  @tracked contacts: UserModel[] = [];
  @tracked secno: string = '';
  @tracked confirmDisconnect: boolean = false;
  @tracked password: string = '';
  @tracked showAllInactive: boolean = false;
  @tracked showAllActive: boolean = false;

  get configuration(): VisualComplianceConfigurationModel | null {
    return this.args.configuration || null;
  }

  get allFlows(): FlowModel[] {
    const { location } = this.args;

    return location ? location.flows.content.slice(0) : [];
  }

  get activeFlows(): FlowModel[] {
    return this.allFlows.filter((flow) => flow.visualComplianceEnabled);
  }

  get inactiveFlows(): FlowModel[] {
    return this.allFlows.filter((flow) => !flow.visualComplianceEnabled);
  }

  constructor(owner: unknown, args: VisualComplianceConfigureComponentSignature['Args']) {
    super(owner, args);
    this.secno = this.args.configuration?.secno || '';
    void this.loadContactsTask.perform();
  }

  uniqUsers(users: UserModel[]): UserModel[] {
    return [...new Map(users.map((u) => [u.id, u])).values()];
  }

  toggleReviewOnMatch = (checked: boolean): void => {
    const { configuration } = this;

    if (!configuration) return;

    configuration.reviewOnMatch = checked;
  };

  updateContacts = (users: UserModel[]): void => {
    const { configuration } = this;

    if (!configuration) return;

    this.contacts = users;

    configuration.contactIds = users.map((u) => u.id);
  };

  connectTask = dropTask(async () => {
    this.metrics.trackJobEvent('Security - Integration Login Button Clicked', {
      integration_name,
      button_text: 'Connect account',
    });

    const { configuration, canConnect } = this;

    if (!(canConnect && configuration)) return;

    try {
      configuration.secno = this.secno;
      configuration.password = this.password;

      this.metrics.trackJobEvent('Security - Integration Login Requested', {
        integration_name,
      });

      await configuration.save();

      if (this.validCredentials) {
        this.metrics.trackJobEvent('Security - Integration Account Connected', { integration_name });
        this.flashMessages.showAndHideFlash('success', 'Connected!');
        this.metrics.trackJobEvent('Viewed Flash Message', {
          type: 'success',
          integration_name,
          message_title: 'Connected!',
          message_code: 'integration_login_success',
          message_code_type,
        });
      }
    } catch (e) {
      configuration.secno = '';
      configuration.password = '';

      const message_title = 'Error connecting Visual Compliance';
      const message_code = parseErrorForDisplay(e);

      this.flashMessages.showFlash('error', message_title, message_code);
      this.metrics.trackJobEvent('Viewed Flash Message', {
        type: 'error',
        integration_name,
        message_title,
        message_code,
        message_code_type,
      });
    }
  });

  disconnectTask = dropTask(async () => {
    this.metrics.trackJobEvent('Security - Integration Logout Button Clicked', {
      integration_name,
      button_text: 'Connect Visual Compliance',
    });

    const { configuration } = this;

    if (!configuration) return;

    configuration.secno = '';
    configuration.password = '';
    this.secno = '';
    this.password = '';

    try {
      this.metrics.trackJobEvent('Security - Integration Logout Requested', {
        integration_name,
      });

      await configuration.save();

      this.metrics.trackJobEvent('Security - Integration Account Disconnected', { integration_name });
      this.flashMessages.showAndHideFlash('success', 'Disconnected!');
      this.metrics.trackJobEvent('Viewed Flash Message', {
        type: 'success',
        integration_name,
        message_title: 'Disconnected!',
        message_code: 'integration_logout_success',
        message_code_type,
      });
    } catch (e) {
      configuration.rollbackAttributes();
      this.secno = configuration.secno;

      const message_title = 'Error disconnecting Visual Compliance';
      const message_code = parseErrorForDisplay(e);

      this.flashMessages.showFlash('error', message_title, message_code);
      this.metrics.trackJobEvent('Viewed Flash Message', {
        type: 'error',
        integration_name,
        message_title,
        message_code,
        message_code_type,
      });
    } finally {
      this.confirmDisconnect = false;
    }
  });

  loadContactsTask = task(async () => {
    const { configuration } = this;

    if (!configuration) return;

    try {
      const contacts = await all(configuration.contactIds.map((id) => this.store.findRecord('user', id)));

      this.contacts = contacts;
    } catch (e) {
      this.flashMessages.showFlash('error', 'Error loading contacts', parseErrorForDisplay(e));
    }
  });

  searchUsersTask = restartableTask(async (term: string) => {
    const { configuration, currentLocation } = this;

    if (!configuration) return [];

    const permissionsFilter = [
      Permission.VISITORS_INVITE_VISUAL_COMPLIANCE_APPROVAL_READ,
      Permission.VISITORS_INVITE_VISUAL_COMPLIANCE_APPROVAL_REVIEW,
      Permission.VISITORS_ENTRY_VISUAL_COMPLIANCE_APPROVAL_READ,
      Permission.VISITORS_ENTRY_VISUAL_COMPLIANCE_APPROVAL_REVIEW,
    ].join(',');

    const queryParams = {
      filter: {
        query: term,
        location: currentLocation.location.id,
        permissions: permissionsFilter,
      },
      include: 'user',
    };

    const re = new RegExp(`.*${term}.*`, 'i');

    const userRoles = await currentLocation.store.query('user-role', queryParams);
    const userPromises = userRoles.map((role: LocationRoleModel | CompanyRoleModel | ZoneRoleModel) => role.user);
    const users = await all(userPromises);
    const uniqueUsers = this.uniqUsers(users);

    const filteredUsers = uniqueUsers.filter(
      ({ id, fullName }) => !configuration.contactIds.map((n) => n.toString()).includes(id) && fullName.match(re),
    );

    return filteredUsers;
  });

  <template>
    <LiquidIf @predicate={{this.loadContactsTask.isRunning}} class="animate-fade-300">
      <:default>
        <div style="max-width: 75%">
          <ol class="mt-8 mb-4 pl-0 plugin-setup-steps">
            <li class="inline-block mr-3 future"> Connect to Visual Compliance</li>
            <li class="inline-block mr-3 future"> Configure</li>
          </ol>
          <div class="w-full mt-8 h-6 py-6 rounded-lg bg-carbon-5"></div>

          <div class="w-1/12 mt-8 h-4 rounded-lg bg-carbon-5"></div>
          <div class="w-10/12 mt-4 h-4 rounded-lg bg-carbon-5"></div>
          <div class="w-full mt-6 h-4 py-2 rounded-lg bg-carbon-5"></div>

          <div class="w-3/12 mt-10 h-4 rounded-lg bg-carbon-5"></div>
          <div class="w-8/12 mt-4 h-4 rounded-lg bg-carbon-5"></div>

          <div class="flex w-full">
            <div class="w-6/12">
              <div class="w-3/12 mt-6 h-4 rounded-lg bg-carbon-5"></div>
              <div class="w-6/12 mt-4 h-4 rounded-lg bg-carbon-5"></div>
            </div>
            <div class="w-6/12">
              <div class="w-3/12 mt-6 h-4 rounded-lg bg-carbon-5"></div>
              <div class="w-6/12 mt-4 h-4 rounded-lg bg-carbon-5"></div>
            </div>
          </div>
        </div>
      </:default>
      <:else>
        <div style="max-width: 75%" data-test-configure-visual-compliance>
          <ol class="mt-8 mb-4 pl-0 plugin-setup-steps">
            <li class="inline-block mr-3 {{if this.isConnected 'completed' 'active'}}">
              <span class="sr-only">{{if this.isConnected "completed step" "current step"}}</span>
              {{#if this.isConnected}}
                <button
                  data-test-visual-compliance-disconnect
                  type="button"
                  class="btn-reset"
                  {{on "click" (fn (mut this.confirmDisconnect) true)}}
                >
                  Connect to Visual Compliance
                </button>
              {{else}}
                Connect to Visual Compliance
              {{/if}}
            </li>
            <li class="inline-block mr-3 {{if this.isConnected 'active' 'future'}}">
              <span class="sr-only">{{if this.isConnected "current step" "next step"}}</span>
              Configure
            </li>
          </ol>

          {{#if this.confirmDisconnect}}
            <ConfirmDirtyTransition
              @bodyMessage="Navigating back will automatically disconnect Visual Compliance for this location. Are you sure you want to continue?"
              @confirm={{this.disconnectTask.perform}}
              @cancel={{fn (mut this.confirmDisconnect) false}}
            />
          {{/if}}

          {{#if (and this.isConnected this.connectTask.isIdle)}}
            <MessageBox @type="info" class="mt-8 max-w-full">
              <p class="mb-0">You can choose which visitor information to check and which lists of restricted parties to
                check visitors against in your
                <a href="http://www.visualcompliance.com/" target="_blank" rel="noopener">Visual Compliance account</a>.</p>
            </MessageBox>

            <div class="mt-8" data-test-visual-compliance-review>
              <h5 class="text-base pb-2">Visitor entry</h5>
              <label class="block label-reset text-sm">
                <input
                  data-test-visual-compliance-review
                  type="checkbox"
                  class="mx-1"
                  name="reviewOnMatch"
                  checked={{@configuration.reviewOnMatch}}
                  disabled={{@readOnly}}
                  {{on "change" (pick "target.checked" this.toggleReviewOnMatch)}}
                />
                Block visitor invites and entries if there is a Visual Compliance match.
                <span>
                  {{svgJar "gray-info" width="16" height="16" class="-mb-0.5 ml-1 cursor-pointer"}}
                  <EmberPopover
                    @side="top"
                    @tooltipClass="!rounded-none !p-2 !border !border-carbon-20 !text-carbon-80 !bg-white !text-xs text-shadow-none !max-w-[482px]"
                    @arrowClass="hidden"
                    @popperContainer="body"
                    @popoverHideDelay={{300}}
                  >
                    If there is a match, admins will need to approve visitors before they are signed in and hosts are
                    notified. Go to
                    <a
                      href="https://www.visualcompliance.com"
                      target="_blank"
                      rel="noopener"
                      class="!text-gem-50"
                    >Visual Compliance to learn&nbsp;more&nbsp;→</a>
                  </EmberPopover>
                </span>
              </label>
            </div>

            <div class="mt-8" data-test-visual-compliance-alerts>
              <h5 class="text-base pb-2">Alerts</h5>
              <p>Choose which admins to alert when a visitor matches a restricted party. These admins can also approve
                or deny flagged visitors.</p>
              <PowerSelectMultiple
                @ariaLabel="Choose admins to receive visual compliance alerts"
                @search={{this.searchUsersTask.perform}}
                @searchEnabled={{true}}
                @searchField="fullName"
                @placeholder="Search administrators"
                @allowClear={{true}}
                @selected={{this.contacts}}
                @onChange={{this.updateContacts}}
                as |contact|
              >
                {{contact.fullName}}
              </PowerSelectMultiple>
            </div>

            <div class="mt-8" data-test-visual-compliance-flows>
              <h5 class="text-base pb-2">Visitor types</h5>
              <p>Enable Visual Compliance for each visitor type you want to screen.</p>

              <div class="flex pt-2">
                <div class="w-6/12">
                  <h6 class="text-carbon-50 text-xs uppercase mb-2" data-test-visual-compliance-active-heading>
                    Screening ({{this.activeFlows.length}})
                  </h6>
                  <ul class="list-none p-0 m-0 leading-normal" data-test-visual-compliance-flows-active>
                    {{#each (if this.showAllActive this.activeFlows (take 5 this.activeFlows)) as |flow|}}
                      <li>
                        <LinkTo
                          @route="visitors.settings.visitor-types.flow.security"
                          @model={{flow.id}}
                          class="block py-1"
                        >
                          <GlobalFlowIcon @show={{flow.isGlobalChild}} class="inline">
                            {{flow.name}}
                          </GlobalFlowIcon>
                          <span class="ml-1">→</span>
                        </LinkTo>
                      </li>
                    {{else}}
                      <li class="text-carbon-40 text-sm pt-1" data-test-visual-compliance-empty-active>
                        Not enabled for any visitor types yet.
                      </li>
                    {{/each}}
                  </ul>
                  {{#if (and (not this.showAllActive) (gt this.activeFlows.length 5))}}
                    <button
                      data-test-visual-compliance-show-more-active
                      type="button"
                      class="btn-reset text-sm text-carbon-40 mt-2 py-2"
                      {{on "click" (fn (mut this.showAllActive) true)}}
                    >
                      Show more
                    </button>
                  {{/if}}
                </div>
                <div class="w-6/12">
                  <h6 class="text-carbon-50 text-xs uppercase mb-2" data-test-visual-compliance-inactive-heading>
                    Not screening ({{this.inactiveFlows.length}})
                  </h6>
                  <ul class="list-none p-0 m-0 leading-normal" data-test-visual-compliance-flows-inactive>
                    {{#each (if this.showAllInactive this.inactiveFlows (take 5 this.inactiveFlows)) as |flow|}}
                      <li>
                        <LinkTo
                          @route="visitors.settings.visitor-types.flow.security"
                          @model={{flow.id}}
                          class="block py-1"
                        >
                          {{flow.name}}
                          →
                        </LinkTo>
                      </li>
                    {{else}}
                      <li class="text-carbon-40 text-sm pt-1" data-test-visual-compliance-empty-inactive>
                        All visitor types enabled.
                      </li>
                    {{/each}}
                    {{#if (and (not this.showAllInactive) (gt this.inactiveFlows.length 5))}}
                      <button
                        data-test-visual-compliance-show-more-inactive
                        type="button"
                        class="btn-reset text-sm text-carbon-40 mt-2 py-2"
                        {{on "click" (fn (mut this.showAllInactive) true)}}
                      >
                        Show more
                      </button>
                    {{/if}}
                  </ul>
                </div>
              </div>
            </div>
          {{else}}
            {{#if (and (not this.didAttemptConnect) @configuration.credentialsInvalid @configuration.secno)}}
              <MessageBox @type="warning" class="max-w-full my-8" data-test-visual-compliance-invalid-credentials>
                Your connection to Visual Compliance has timed out. Reconnect by entering your security number and
                password, or email
                <a href="mailto:VCforEnvoy@descartes.com">VCforEnvoy@descartes.com</a>
                to get a new set of credentials.
              </MessageBox>
            {{else}}
              <p>To connect Envoy to your Visual Compliance account, email
                <a href="mailto:VCforEnvoy@descartes.com">VCforEnvoy@descartes.com</a>
                to get a unique security number and password, then enter these below.</p>
            {{/if}}

            {{! autocomplete: https://bugs.chromium.org/p/chromium/issues/detail?id=468153#c164 }}
            <form class="flex flex-row" autocomplete="off" {{on "submit" (preventDefault this.connectTask.perform)}}>
              <label class="pr-4" style="min-width: 33%;">
                Security Number (Secno)
                <span class="requiredField"></span>
                <input
                  data-test-visual-compliance-secno
                  type="text"
                  name="visual-compliance-secno"
                  autocomplete="visual-compliance-secno"
                  class={{if (and this.didAttemptConnect (isEmpty this.secno)) "error"}}
                  value={{this.secno}}
                  {{on "input" (pick "target.value" (set this "secno"))}}
                />
                {{#if this.didAttemptConnect}}
                  {{#if (isEmpty this.secno)}}
                    <div class="label-reset bottom-error absolute" data-test-visual-compliance-error-empty-secno>
                      Secno required.
                    </div>
                  {{else if (and @configuration.credentialsInvalid this.connectTask.isIdle)}}
                    <div
                      class="label-reset bottom-error absolute"
                      data-test-visual-compliance-error-credentials-invalid
                    >
                      Your security number or password is incorrect
                    </div>
                  {{/if}}
                {{/if}}
              </label>

              <label class="pr-4" style="min-width: 33%;">
                Password
                <span class="requiredField"></span>
                <input
                  data-test-visual-compliance-password
                  type="password"
                  name="visual-compliance-password"
                  autocomplete="visual-compliance-password"
                  class={{if (and this.didAttemptConnect (isEmpty this.password)) "error"}}
                  value={{this.password}}
                  {{on "input" (pick "target.value" (set this "password"))}}
                />
                {{#if (and this.didAttemptConnect (isEmpty this.password))}}
                  <div class="label-reset bottom-error absolute" data-test-visual-compliance-error-empty-password>
                    Password required.
                  </div>
                {{/if}}
              </label>
              <div>
                <button
                  data-test-visual-compliance-connect
                  type="submit"
                  class="btn btn--brand btn--small mt-4"
                  disabled={{and this.didAttemptConnect (not this.canConnect)}}
                >
                  {{#if this.connectTask.isRunning}}
                    <img class="pl-2 pr-2" src="/assets/images/progress-small-white.svg" alt="" role="presentation" />
                  {{else}}
                    Connect account
                  {{/if}}
                </button>
              </div>
            </form>
          {{/if}}
        </div>
      </:else>
    </LiquidIf>
  </template>
}
